import React, {
  memo,
  useMemo,
  useCallback,
  useRef,
  useState,
  createContext,
} from "react";
import {
  motion,
  useMotionValue,
  Reorder,
  useDragControls,
} from "framer-motion";
import useStore, { useData, useSchema } from "../../hooks/useStore";
import { ListRenderer, PanelRenderer } from "./data/List";
import {
  getPathComponents,
  canIdBeWildcard,
  getDataIdsAt,
  pathFromComponents,
  updateComponent,
  flattenLocatedData,
  prettierTitle,
  appendPath,
} from "./utils";
import { Book, BookOpen, Database, EyeOff, Paintbrush } from "lucide-react";
import isEqual from "react-fast-compare";

export const ViewContext = createContext();

export const View = memo(
  ({
    view: { infoObject, path, viewType, vid, options: viewOptions },
    viewData,
  }) => {
    const schema = useSchema(
      infoObject,
      viewOptions.flattened ? appendPath(path, "[*]") : path
    );

    const data = useMemo(() => {
      return viewOptions.flattened ? flattenLocatedData(viewData) : viewData;
    }, [viewData, viewOptions.flattened]);

    const isChildView = path.length > 0;

    const dragControls = useDragControls();
    const width = useMotionValue(isChildView ? 400 : 500); // default width
    const viewRef = useRef(null);

    const removeView = useStore((state) => state.removeView);
    const updateView = useStore((state) => state.updateView);

    const viewContext = useMemo(
      () => ({
        rendered: viewOptions.rendered,
        flattened: viewOptions.flattened,
      }),
      [viewOptions]
    );

    const viewRenderer = useMemo(() => {
      const pathKey = path.length > 0 ? path : "ROOT";

      switch (viewType) {
        case "list":
          return (
            <ListRenderer
              key={`r-view-${infoObject}-${pathKey}`}
              infoObjectName={infoObject}
              path={path}
              name={infoObject}
              data={data}
              schema={schema}
              options={{
                hideKeys: true,
              }}
              is={{
                view: true,
                floatingWindow: false,
                previewView: false,
              }}
            />
          );

        case "panel":
          return (
            <PanelRenderer
              key={`r-view-${infoObject}-${pathKey}`}
              infoObjectName={infoObject}
              path={path}
              name={infoObject}
              data={data}
              schema={schema}
              options={{
                hideKeys: true,
              }}
            />
          );

        default:
          return null;
      }
    }, [path, viewType, infoObject, data, schema]);

    const startResize = useCallback(
      (mouseDownEvent) => {
        mouseDownEvent.stopPropagation();
        mouseDownEvent.preventDefault();

        const startX = mouseDownEvent.pageX;
        const startWidth = width.get();

        const doDrag = (e) => {
          e.stopPropagation();
          e.preventDefault();

          const newWidth = Math.max(300, startWidth + e.pageX - startX);
          width.set(newWidth);

          return false;
        };

        const stopDrag = () => {
          document.removeEventListener("mousemove", doDrag);
          document.removeEventListener("mouseup", stopDrag);
        };

        document.addEventListener("mousemove", doDrag);
        document.addEventListener("mouseup", stopDrag);
      },
      [width]
    );

    return (
      <Reorder.Item
        key={`view-${vid}`}
        ref={viewRef}
        value={vid}
        className="r-view"
        style={{ width }}
        dragListener={false}
        dragControls={dragControls}
      >
        <div
          className="r-view-header"
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            dragControls.start(e);
          }}
        >
          <span className="r-view-type">{prettierTitle(viewType)}</span>
          <ViewTitle infoObject={infoObject} path={path} viewId={vid} />
          <div className="r-view-header-icons">
            <div
              className="r-icon r-icon-clickable r-icon-warning"
              onClick={() => {
                updateView(vid, {
                  options: {
                    ...viewOptions,
                    rendered: !viewOptions.rendered,
                  },
                });
              }}
            >
              {viewOptions.rendered ? (
                <Database size={12} />
              ) : (
                <Paintbrush size={12} />
              )}
            </div>
            {isChildView && (
              <>
                <div
                  className="r-icon r-icon-clickable r-icon-warning"
                  onClick={() => {
                    updateView(vid, {
                      options: {
                        ...viewOptions,
                        flattened: !viewOptions.flattened,
                      },
                    });
                  }}
                >
                  {viewOptions.flattened ? (
                    <BookOpen size={12} />
                  ) : (
                    <Book size={12} />
                  )}
                </div>
                <div
                  className="r-icon r-icon-clickable r-icon-warning"
                  onClick={() => {
                    removeView(vid);
                  }}
                >
                  <EyeOff size={12} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="r-view-content">
          <ViewContext.Provider value={viewContext}>
            {viewRenderer}
          </ViewContext.Provider>
        </div>

        <div className="r-view-resizer" onMouseDown={startResize} />
      </Reorder.Item>
    );
  },
  isEqual
);

const ViewTitle = memo(({ infoObject, path, viewId }) => {
  const viewNameComponents = getPathComponents(infoObject, path);

  return (
    <span className="r-view-name">
      {viewNameComponents.slice(1, -1).map((component) => {
        const { type, value, order } = component;

        return type !== "id" ? (
          <ViewTitleKey key={`${value}-${order}-key`} component={component} />
        ) : (
          <ViewTitleId
            key={`${value}-${order}-id`}
            infoObjectName={infoObject}
            components={viewNameComponents}
            order={order}
            viewId={viewId}
          />
        );
      })}
      <span className="r-view-name-main">
        {prettierTitle(viewNameComponents[viewNameComponents.length - 1].value)}
      </span>
    </span>
  );
}, isEqual);

const ViewTitleKey = memo(({ component }) => {
  const title = prettierTitle(component.value);
  const displayTitle = title.length > 16 ? `${title.slice(0, 15)}.` : title;
  return <span className="r-view-name-pre">{displayTitle}</span>;
}, isEqual);

const ViewTitleId = memo(({ infoObjectName, components, order, viewId }) => {
  const allData = useData();
  const allSchema = useStore((state) => state.getInfoObjects());

  const updateView = useStore((state) => state.updateView);

  const pathToId = pathFromComponents(components, order - 1);
  // const canBeWildcard = canIdBeWildcard(order, components);

  const ids = getDataIdsAt(
    allData,
    allSchema,
    infoObjectName,
    pathToId,
    // canBeWildcard
    true
  );

  const [isOpen, setIsOpen] = useState(false);

  const { value } = components.find((c) => c.order === order);
  const currentValue = value.replace(/^\[|\]$/g, "");

  return (
    <div className="r-view-name-pre custom-dropdown">
      <span>[</span>
      <motion.span
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: "pointer" }}
        initial={false}
        animate={{
          fontSize: isOpen ? "1.25rem" : "0.75rem",
          color: isOpen ? "#333" : "#999",
          margin: isOpen ? "0 0.5rem" : "0",
        }}
        whileHover={{
          color: "#333",
        }}
        transition={{ duration: 0.1 }}
      >
        {currentValue}
      </motion.span>
      <span>]</span>
      {isOpen && (
        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="dropdown-menu"
        >
          {ids.map((id) => (
            <li
              key={id}
              className={id === currentValue ? "selected" : ""}
              onClick={() => {
                setIsOpen(false);
                updateView(viewId, {
                  path: pathFromComponents(
                    updateComponent(components, order, {
                      type: "id",
                      value: `[${id}]`,
                      order,
                    })
                  ),
                });
              }}
            >
              {id}
            </li>
          ))}
        </motion.ul>
      )}
    </div>
  );
}, isEqual);
